import React, { FC, useEffect, useMemo } from 'react'
import { observer, useLocalObservable } from 'mobx-react-lite'
import { reaction } from 'mobx'
import clsx from 'clsx'

import { Box, FormHelperText, FormLabel } from '@material-ui/core'

import search from '../assets/search.svg'

import { MyServerTable } from './MyServerTable'
import { BootstrapInput } from './BootstrapInput'
import { ReactiveTab, ReactiveTabs } from './ReactiveTabs'
import { OrdersTopPagination } from './OrdersTopPagination'
import { PullOrdersWithErrorsButton } from './PullOrdersWithErrorsButton'
import { ExportFileKind, ExportToExcelButton } from './ExportToExcelButton'

import { useOrdersTableStyles } from '../hooks/ordersTableStylesHook'
import { useOrderSearchStyles } from '../hooks/ordersSearchStylesHook'

import { EnumValue, Required2, UnprocessedValueError } from '../common/tsUtils'

import { ServerTableData } from '../store/serverTableData'

import { MyTableProps } from '../types/tableTypes'
import { StoreAccountBaseProps } from '../types/accountProps'

import { OrderModelBase } from '../server/mpsklad_core/Models/Orders/OrderModelBase'
import { StoreAccountModelBase } from '../server/mpsklad_core/Models/StoreAccountModelBase'
import { OrdersTableFilterColumn } from '../server/mpsklad_core/Models/OrdersTableFilterColumn'
import { StoreOrdersTablePageOptions } from '../server/mpsklad_core/Models/StoreOrdersTablePageOptions'

export type OrdersTable<TRow extends OrderModelBase, TColumnId extends string> =
  ServerTableData<TRow, TColumnId, typeof OrdersTableFilterColumn, never>

/**
 * Strings to forbid implicit type casting to index/numbers.
 */
export enum OrdersTableTab {
  Active = 'Active',
  Cancelled = 'Cancelled',
  WithErrors = 'WithErrors',
  LoadOrders = 'LoadOrders',
}

export const ordersTableTabNames: {
  [K in EnumValue<typeof OrdersTableTab>]: string
} = {
  [OrdersTableTab.Active]: 'Активные',
  [OrdersTableTab.Cancelled]: 'Отменённые',
  [OrdersTableTab.WithErrors]: 'С ошибками',
  [OrdersTableTab.LoadOrders]: 'Загрузка заказов'
}

export type StoreOrdersScreenProps<TAccount extends StoreAccountModelBase, TRow extends OrderModelBase, TColumnId extends string> =
  StoreAccountBaseProps<TAccount>
  & {
  tabValues: readonly [OrdersTableTab.Active, ...OrdersTableTab[]]

  orders: OrdersTable<TRow, TColumnId>

  columns: MyTableProps<TColumnId, TRow>['columns']

  searchPlaceholder: string

  pullOrdersWithErrors: (orderId: number) => Promise<void>

  exportOrdersToExcel: (options: StoreOrdersTablePageOptions) => Promise<Blob>

  LoadOrdersFormComponent: FC<StoreAccountBaseProps<TAccount>>

  AfterSearchActiveComponent?: FC<StoreAccountBaseProps<TAccount> & {orders: OrdersTable<TRow, TColumnId>}>
}

export const StoreOrdersScreen =
  observer(
    <TAccount extends StoreAccountModelBase, TRow extends OrderModelBase, TColumnId extends string>(
      {
        account, tabValues,
        orders, columns,
        searchPlaceholder, pullOrdersWithErrors, exportOrdersToExcel,
        LoadOrdersFormComponent, AfterSearchActiveComponent
      }: StoreOrdersScreenProps<TAccount, TRow, TColumnId>) => {
      const classes = useOrderSearchStyles()
      const tableClasses = useOrdersTableStyles()

      const tabs =
        useMemo<ReactiveTab<OrdersTableTab>[]>(
          () => tabValues.map(value => ({value, title: ordersTableTabNames[value]})),
          [tabValues])

      const selectedTab =
        useLocalObservable<{value: OrdersTableTab}>(
          () => ({value: OrdersTableTab.Active}))

      useEffect(() =>
          // Don't autorun - loading changes deep state, causing cyclic reactions
          reaction(
            () => selectedTab.value,
            () => {
              orders.resetPropFilters()

              switch (selectedTab.value) {
                case OrdersTableTab.Active:
                  orders.setPropFilter('hasError', JSON.stringify(false))
                  break

                case OrdersTableTab.Cancelled:
                  orders.setPropFilter('isCancelled', JSON.stringify(true))
                  break

                case OrdersTableTab.WithErrors:
                  orders.setPropFilter('hasError', JSON.stringify(true))
                  break

                case OrdersTableTab.LoadOrders:
                  return

                default:
                  throw new UnprocessedValueError('OrdersTableTab', selectedTab.value)
              }

              orders.reload(true)
            },
            {fireImmediately: true}),
        [orders, selectedTab])

      const onTabChange = (newValue: OrdersTableTab, _event: React.ChangeEvent<{}>): void => {
        selectedTab.value = newValue
      }

      return (
        <ReactiveTabs tabs={tabs} value={selectedTab.value} onChange={onTabChange}>
          {
            selectedTab.value === OrdersTableTab.LoadOrders
            ?
            <Box display="flex" justifyContent="flex-start" className={classes.container}>
              <Box maxWidth={480}>
                <Box marginBottom={1}>
                  <FormLabel>
                    Загрузка заказов
                  </FormLabel>
                </Box>

                <LoadOrdersFormComponent account={account}/>
              </Box>
            </Box>
            : <>
              <Box display="flex" flexDirection="column" className={classes.container}>
                <Box display="flex" alignItems="flex-start">
                  <Box>
                    <Box position="relative">
                      <Box minWidth={350} maxWidth={350}>
                        <FormLabel className={classes.root}>Поиск</FormLabel>

                        <BootstrapInput
                          fullWidth
                          id={`search-${account.integrationType}-orders`}
                          onChange={e => orders.setGlobalFilter(e.target.value)}
                        />

                        <FormHelperText className={classes.root}>
                          {searchPlaceholder}
                        </FormHelperText>
                      </Box>

                      <img className={clsx('search-icon', classes.searchIcon)} src={search} alt="Поиск"/>
                    </Box>
                  </Box>

                  <Box marginLeft={3} marginTop={'15px'}>
                    <ExportToExcelButton
                      fileKind={ExportFileKind.Orders}
                      columns={columns}
                      accountName={account.name}
                      makeExportRequest={
                        columnIds => exportOrdersToExcel({
                            ...orders.getExportOptions(),
                            accountId: account.id,
                            columnIds,
                            orderIds: orders.selectedRows.map(_ => _.id)
                          } satisfies Required2<StoreOrdersTablePageOptions>
                        )}
                    />
                  </Box>

                  {
                    selectedTab.value === OrdersTableTab.WithErrors &&
                    <Box marginLeft={1} marginRight={30} marginTop={'15px'}>
                      <PullOrdersWithErrorsButton
                        ordersTable={orders}
                        pullOrders={() => pullOrdersWithErrors(account.id)}
                      />
                    </Box>
                  }

                  <OrdersTopPagination table={orders}/>
                </Box>

                {
                  AfterSearchActiveComponent && selectedTab.value === OrdersTableTab.Active &&
                  <Box marginRight={10}>
                    <AfterSearchActiveComponent account={account} orders={orders}/>
                  </Box>
                }
              </Box>

              <MyServerTable
                hideTopPagination
                classes={tableClasses}
                table={orders}
                columns={columns}
                labelNoRows="Заказов нет"
                labelNotFound="Заказы не найдены"
                labelRowsPerPage="Заказов на странице:"
              />
            </>
          }
        </ReactiveTabs>
      )
    })